function isiOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

function isChromeOnIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && /CriOS/i.test(navigator.userAgent);
  }

function isSafariOnIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent) && !/CriOS/i.test(navigator.userAgent);
  }

export default class AudioController {
    constructor() {
      this.audioContext = null;
      this.audioElement = null;
      this.source = null;
      this.unlocked = false;
      this.isPlaying = false;
      this.currentAudioUrl = null;
      this.isRecreating = false;
      this.isDestroyed = false;
      this.iosAudioContext = null; // iOS 전용 AudioContext
      this.playPromise = null; // iOS에서 play() Promise 추적
      
      // 이벤트 핸들러들을 바인딩
      this.playHandler = () => {
        if (!this.isDestroyed) {
          this.isPlaying = true;
        }
      };
      
      this.pauseHandler = () => {
        if (!this.isDestroyed) {
          this.isPlaying = false;
        }
      };
      
      this.endedHandler = () => {
        if (!this.isDestroyed) {
          this.isPlaying = false;
        }
      };
      
      this.errorHandler = (e) => {
        // 이미 파괴된 상태면 에러 무시
        if (this.isDestroyed) return;
        
        // 에러가 발생해도 음악이 재생되고 있다면 무시 (YouTube 오디오가 작동 중)
        if (this.isPlaying && this.audioElement && !this.audioElement.paused) {
          return;
        }
        
        // 실제 에러인 경우에만 로그 출력
        if (this.audioElement && this.audioElement.error) {
          console.warn('Audio error:', this.audioElement.error.message);
        }
        
        this.isPlaying = false;
        
        // 에러 발생 시 audioElement를 새로 생성 (한 번만)
        if (!this.isRecreating && !this.isDestroyed) {
          this.isRecreating = true;
          this.recreateAudioElement();
          setTimeout(() => {
            this.isRecreating = false;
          }, 1000);
        }
      };
      
      this.loadstartHandler = () => {};
      this.canplayHandler = () => {};
      this.canplaythroughHandler = () => {};
      this.loadeddataHandler = () => {};
    }
  
    async unlock() {
      if (this.unlocked || this.isDestroyed) return;
  
      try {
        // iOS에서는 별도의 AudioContext를 생성하여 사용자 상호작용 시 해제
        if (isiOS()) {
          if (!this.iosAudioContext || this.iosAudioContext.state === 'closed') {
            this.iosAudioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
          
          // iOS에서 AudioContext를 사용자 터치 이벤트에서 resume
          if (this.iosAudioContext.state === 'suspended') {
            await this.iosAudioContext.resume();
          }
          
          // iOS에서는 빈 오디오 버퍼를 재생하여 오디오 컨텍스트 활성화
          const buffer = this.iosAudioContext.createBuffer(1, 1, 22050);
          const source = this.iosAudioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(this.iosAudioContext.destination);
          source.start(0);
          source.stop(0.001);
          
          // iOS 크롬에서는 추가적인 대기 시간 필요
          if (isChromeOnIOS()) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } else {
          // 데스크탑에서는 기존 방식
          if (!this.audioContext || this.audioContext.state === 'closed') {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
          
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }
        }
        
        this.unlocked = true;
        //console.log('Audio context unlocked successfully');
      } catch (e) {
        //console.warn('Audio unlock failed:', e);
      }
    }
  
    setupEventListeners() {
      if (!this.audioElement || this.isDestroyed) return;
      
      this.audioElement.addEventListener('loadstart', this.loadstartHandler);
      this.audioElement.addEventListener('canplay', this.canplayHandler);
      this.audioElement.addEventListener('canplaythrough', this.canplaythroughHandler);
      this.audioElement.addEventListener('loadeddata', this.loadeddataHandler);
      this.audioElement.addEventListener('error', this.errorHandler);
      this.audioElement.addEventListener('play', this.playHandler);
      this.audioElement.addEventListener('pause', this.pauseHandler);
      this.audioElement.addEventListener('ended', this.endedHandler);
    }
    
    async init(audioUrl) {
      if (this.isDestroyed) return;
      
      this.currentAudioUrl = audioUrl;
      
      // <audio> 태그 생성 및 설정
      this.audioElement = new Audio(audioUrl);
      this.audioElement.crossOrigin = "anonymous"; // CORS 문제 방지
      this.audioElement.preload = "auto"; // iOS에서 preload 설정
      
      // iOS 특화 설정
      if (isiOS()) {
        this.audioElement.playsInline = true;
        this.audioElement.webkitPlaysinline = true;
      }
      
      // 이벤트 리스너 설정
      this.setupEventListeners();
      
      try {
        this.audioElement.load();
        
        // iOS에서는 별도 AudioContext 사용, 데스크탑에서는 기존 방식
        if (isiOS()) {
          if (!this.iosAudioContext || this.iosAudioContext.state === 'closed') {
            this.iosAudioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
          this.source = this.iosAudioContext.createMediaElementSource(this.audioElement);
          this.source.connect(this.iosAudioContext.destination);
        } else {
          if (!this.audioContext || this.audioContext.state === 'closed') {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
          this.source = this.audioContext.createMediaElementSource(this.audioElement);
          this.source.connect(this.audioContext.destination);
        }
      } catch (e) {
        console.warn('Audio init error:', e);
      }
    }
  
    async play() {
      if (!this.audioElement || this.isDestroyed) {
        return;
      }
      
      // 오디오 에러 상태 확인
      if (this.audioElement.error) {
        return;
      }
      
      try {
        // iOS에서 AudioContext 상태 확인 및 복구
        if (isiOS() && this.iosAudioContext) {
          if (this.iosAudioContext.state === 'suspended') {
            await this.iosAudioContext.resume();
          }
        } else if (this.audioContext) {
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }
        }
        
        // readyState가 HAVE_ENOUGH_DATA가 될 때까지 대기
        if (this.audioElement.readyState < 4) { // HAVE_ENOUGH_DATA
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Audio loading timeout')), 10000);
            const checkReady = () => {
              if (this.audioElement.readyState >= 4) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(checkReady, 100);
              }
            };
            checkReady();
          });
        }
        
        // iOS에서는 play() 호출 전에 추가 대기 및 상태 확인
        if (isiOS()) {
          // iOS 크롬에서는 더 긴 대기 시간 필요
          const waitTime = isChromeOnIOS() ? 250 : 150;
          await new Promise(resolve => setTimeout(resolve, waitTime));
          
          // iOS에서 오디오가 이미 재생 중인지 확인
          if (!this.audioElement.paused && this.audioElement.currentTime > 0) {
            this.isPlaying = true;
            return;
          }
        }
        
        // 이전 play() Promise가 있다면 완료 대기
        if (this.playPromise) {
          try {
            await this.playPromise;
          } catch (e) {
            // 이전 Promise 에러는 무시하고 계속 진행
          }
        }
        
        // iOS 크롬에서는 특별한 재생 로직
        if (isChromeOnIOS()) {
          // iOS 크롬에서는 currentTime을 0으로 설정 후 재생
          this.audioElement.currentTime = 0;
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        // iOS에서 play() Promise 추적
        this.playPromise = this.audioElement.play();
        await this.playPromise;
        this.playPromise = null;
        
        this.isPlaying = true;
        //console.log('Audio play successful');
      } catch (e) {
        this.playPromise = null;
        
        // 이미 재생 중이거나 파괴된 상태면 에러 무시
        if (this.isPlaying || this.isDestroyed) {
          return;
        }
        //console.warn('Audio play failed:', e);
        this.isPlaying = false;
        
        // iOS에서 특별한 에러 처리
        if (isiOS() && e.name === 'NotAllowedError') {
          //console.warn('iOS audio permission denied, trying alternative method');
          // iOS에서 권한이 거부된 경우 다시 시도
          setTimeout(() => {
            if (!this.isDestroyed) {
              this.audioElement.play().catch(() => {});
            }
          }, 1000);
        }
        
        // iOS 크롬에서 특별한 에러 처리
        if (isChromeOnIOS()) {
          //console.warn('iOS Chrome audio play failed, trying alternative method');
          // iOS 크롬에서는 더 긴 대기 후 재시도
          setTimeout(async () => {
            if (!this.isDestroyed) {
              try {
                this.audioElement.currentTime = 0;
                await this.audioElement.play();
                this.isPlaying = true;
              } catch (retryErr) {
                //console.warn('iOS Chrome retry failed:', retryErr);
              }
            }
          }, 1500);
        }
      }
    }
  
    stop() {
      if (this.audioElement && !this.isDestroyed) {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.isPlaying = false;
      }
    }
    
    reset() {
      if (this.audioElement && !this.isDestroyed) {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.isPlaying = false;
      }
    }
  
    getCurrentTime() {
      if (!this.audioElement || this.isDestroyed) {
        return 0;
      }
      
      // iOS에서 오디오 상태 더 정확하게 확인
      const isActuallyPlaying = !this.audioElement.paused && 
                               !this.audioElement.ended && 
                               this.audioElement.currentTime > 0 &&
                               this.audioElement.readyState >= 2; // HAVE_CURRENT_DATA 이상
      
      // isPlaying 상태와 실제 상태가 다르면 동기화
      if (this.isPlaying !== isActuallyPlaying) {
        this.isPlaying = isActuallyPlaying;
        
        // iOS에서 일시정지된 경우 자동 재시도 (파괴되지 않은 상태에서만)
        if (isiOS() && this.audioElement.paused && 
            !this.audioElement.ended && 
            this.audioElement.currentTime > 0 && 
            !this.isDestroyed &&
            this.audioElement.readyState >= 2) {
          
          // iOS에서는 약간의 지연 후 재시도
          setTimeout(() => {
            if (!this.isDestroyed && this.audioElement.paused) {
              this.resumeAudio();
            }
          }, 100);
        }
      }
      
      const currentTime = this.audioElement.currentTime || 0;
      return currentTime;
    }
  
    isRunning() {
      return this.isPlaying;
    }
    
    async resumeAudio() {
      if (!this.audioElement || this.audioElement.ended || this.isDestroyed) {
        return;
      }
      
      try {
        // iOS에서 AudioContext 상태 확인
        if (isiOS() && this.iosAudioContext && this.iosAudioContext.state === 'suspended') {
          await this.iosAudioContext.resume();
        } else if (this.audioContext && this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }
        
        await this.audioElement.play();
        this.isPlaying = true;
      } catch (e) {
        // 이미 재생 중이면 에러 무시
        if (this.isPlaying) {
          return;
        }
        //console.warn('Audio resume failed:', e);
        this.isPlaying = false;
      }
    }
    
    recreateAudioElement() {
      if (this.isDestroyed) return;
      
      try {
        // 기존 audioElement 정리
        if (this.audioElement) {
          this.audioElement.pause();
          this.audioElement.removeEventListener('play', this.playHandler);
          this.audioElement.removeEventListener('pause', this.pauseHandler);
          this.audioElement.removeEventListener('ended', this.endedHandler);
          this.audioElement.removeEventListener('error', this.errorHandler);
          this.audioElement.removeEventListener('loadstart', this.loadstartHandler);
          this.audioElement.removeEventListener('canplay', this.canplayHandler);
          this.audioElement.removeEventListener('canplaythrough', this.canplaythroughHandler);
          this.audioElement.removeEventListener('loadeddata', this.loadeddataHandler);
        }
        
        if (this.source) {
          this.source.disconnect();
          this.source = null;
        }
        
        // iOS에서는 별도 AudioContext 사용
        if (isiOS()) {
          if (!this.iosAudioContext || this.iosAudioContext.state === 'closed') {
            this.iosAudioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
        } else {
          if (!this.audioContext || this.audioContext.state === 'closed') {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          }
        }
        
        // 새 audioElement 생성
        this.audioElement = new Audio(this.currentAudioUrl);
        this.audioElement.crossOrigin = "anonymous";
        this.audioElement.preload = "auto";
        
        // iOS 특화 설정
        if (isiOS()) {
          this.audioElement.playsInline = true;
          this.audioElement.webkitPlaysinline = true;
        }
        
        // 이벤트 리스너 다시 등록
        this.setupEventListeners();
        
        // AudioContext에 다시 연결
        if (isiOS()) {
          this.source = this.iosAudioContext.createMediaElementSource(this.audioElement);
          this.source.connect(this.iosAudioContext.destination);
        } else {
          this.source = this.audioContext.createMediaElementSource(this.audioElement);
          this.source.connect(this.audioContext.destination);
        }
      } catch (e) {
        //console.warn('Failed to recreate audioElement:', e);
      }
    }
    
    destroy() {
        this.isDestroyed = true;
        
        try {
          if (this.audioElement) {
            this.audioElement.pause();
            this.audioElement.src = '';
            this.audioElement.load(); // 브라우저가 메모리에서 해제하도록 유도
            this.audioElement = null;
          }
      
          if (this.source) {
            this.source.disconnect();
            this.source = null;
          }
      
          if (this.audioContext) {
            if (this.audioContext.state !== 'closed') {
              this.audioContext.close();
            }
            this.audioContext = null;
          }
          
          if (this.iosAudioContext) {
            if (this.iosAudioContext.state !== 'closed') {
              this.iosAudioContext.close();
            }
            this.iosAudioContext = null;
          }
      
          this.unlocked = false;
          this.isPlaying = false;
        } catch (e) {
          //console.warn('AudioController destroy error:', e);
        }
      }
  }
